import { useEffect, useRef } from "react";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { useQuery } from "@/lib/convex";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Skeleton,
  Stack,
  Text,
  Timeline,
} from "@chakra-ui/react";
import { LuArrowLeft } from "react-icons/lu";
import { DetailSkeleton } from "../components/LoadingSkeletons";
import { statusPresentation } from "../components/activityStatus";
import { PageHeader } from "../components/PageHeader";
import { useEffectivePersonId } from "../impersonation";
import { ActivityTaskCard } from "./ActivityTaskCard";
import {
  ActivityRefs,
  TagBadge,
  formatDay,
  localToday,
  stepWaitsOnViewer,
  type AssignmentDetail,
} from "./ActivityDetailParts";
import { ActivityHistory } from "./ActivityHistory";
import { activityTrail } from "./activityOrigin";

/**
 * Whose activity this is, in the header, for anybody but them.
 *
 * A reviewer opening an activity from the queue is judging one particular
 * person's work, and the name used to sit in a small tile beside the dates.
 */
function AssigneeBlock({ subject }: { subject: NonNullable<AssignmentDetail["subject"]> }) {
  const name = <Text fontWeight="semibold" lineHeight="1.2">{subject.fullName}</Text>;
  return (
    <HStack gap={3}>
      <Avatar.Root size="lg">
        <Avatar.Fallback name={subject.fullName} />
        {subject.profileImageUrl ? (
          <Avatar.Image src={subject.profileImageUrl} alt={`${subject.fullName} profile`} />
        ) : null}
      </Avatar.Root>
      <Stack gap={0.5} minW={0}>
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">
          Assigned to
        </Text>
        {subject.shortName ? (
          <Link to="/cemmies/$shortName" params={{ shortName: subject.shortName }}>
            <Box _hover={{ textDecoration: "underline" }}>{name}</Box>
          </Link>
        ) : name}
        {subject.groupName ? <Text fontSize="sm" color="fg.muted">{subject.groupName}</Text> : null}
      </Stack>
    </HStack>
  );
}

/**
 * What the activity is at, in one line under its name: its state, what is
 * expected and when, and what it is filed under.
 *
 * In the header rather than in a card of its own: a card of three facts
 * between the header and the steps read as detached from both. It was three
 * cards before that -- the state with two boxed dates, a Dependencies card and
 * a Revision notes card -- and the last two usually said that there was
 * nothing to say. A revision note is said in the step it was given on, where
 * it is acted on.
 */
function ActivityFacts({ detail, todayDay }: { detail: AssignmentDetail; todayDay: string }) {
  // Somebody else's activity, waiting on the viewer: the co-supervisor whose
  // step is next, the supervisor who signs. "Waiting on someone else" was
  // true of the activity and wrong for the one person reading it. On their
  // own activity the stored status already speaks to them.
  const isOwn = detail.subject === null || detail.subject.isViewer;
  const waitingOnYou = !isOwn && detail.tasks.some(stepWaitsOnViewer);
  const overdue = detail.dueDay !== null && detail.dueDay < todayDay && detail.status !== "completed";
  const look = statusPresentation(waitingOnYou ? "waiting_on_you" : detail.status, overdue);
  // Only a prerequisite makes "blocked" mean "waiting on another activity";
  // otherwise it is waiting for its opening day. Cancelled and archived are
  // not in the colour scheme and keep the server's word.
  const keepsServerLabel = !waitingOnYou && !overdue && (
    (detail.status === "blocked" && detail.blockedBy.length === 0)
    || detail.status === "cancelled"
    || detail.status === "archived"
  );

  const facts = [
    detail.dueDay === null ? null : `Due ${formatDay(detail.dueDay)}`,
    detail.opensOnDay !== null && detail.opensOnDay > todayDay ? `Opens ${formatDay(detail.opensOnDay)}` : null,
    detail.recommendedStartDay === null ? null : `Start from ${formatDay(detail.recommendedStartDay)}`,
  ].filter((fact) => fact !== null);

  return (
    <HStack flexWrap="wrap" gap={3} rowGap={2}>
      <Badge colorPalette={look.tone} variant="subtle" gap={1.5}>
        <Icon as={look.icon} boxSize={3.5} />
        {keepsServerLabel ? detail.statusLabel : look.label}
      </Badge>
      {/* Always said, in words: required is the usual case, and still
          worth reading. */}
      <Text fontSize="sm" color="fg.muted">
        <Text as="span" fontWeight="semibold">{detail.required ? "Required" : "Encouraged"}</Text>
        {facts.map((fact) => ` · ${fact}`).join("")}
      </Text>
      {detail.tagSnapshots.map((tag) => (
        <TagBadge key={tag.tagId} name={tag.name} color={tag.color} title={tag.key} />
      ))}
    </HStack>
  );
}

/**
 * A dependency is an order of COMPLETION, not permission to begin: the steps
 * below stay open. Said plainly above them, because an activity whose every
 * step is approved and which is still not complete otherwise reads as broken.
 */
function DependencyNote({ detail }: { detail: AssignmentDetail }) {
  if (detail.status !== "blocked" || detail.blockedBy.length === 0) {
    return null;
  }
  return (
    <Box borderWidth="1px" borderColor="orange.muted" bg="orange.subtle" px={4} py={3}>
      <Text fontSize="sm">
        {detail.allStepsDone
          ? "Every step here is done. This cannot be completed until "
          : "This is completed after "}
        <ActivityRefs items={detail.blockedBy} />
        {detail.allStepsDone
          ? " is."
          : ". You can still work on the steps below in the meantime."}
      </Text>
    </Box>
  );
}

export function ActivityDetailPage() {
  const todayDay = localToday();

  const params = useParams({ from: "/_authed/activities/$personActivityId", structuralSharing: true });
  const origin = useSearch({ from: "/_authed/activities/$personActivityId" });
  const effectivePersonId = useEffectivePersonId();
  const detail = useQuery(api.personActivities.getPersonActivityDetail, {
    personActivityId: params.personActivityId as Id<"personActivities">,
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });
  // Where the trail and the back button lead, by where the page was opened from.
  const { crumbs, back } = activityTrail(origin, {
    personId: detail?.personId ?? "",
    name: detail?.subject?.fullName ?? null,
  });

  // Opened with a step waiting on the viewer further down: go to it. Once
  // per activity, so the page does not jump back there after every save --
  // and not when it is the first step, which is already in view below the
  // summary.
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (detail === undefined || scrolledFor.current === detail._id) {
      return;
    }
    scrolledFor.current = detail._id;
    const index = detail.tasks.findIndex(stepWaitsOnViewer);
    if (index > 0) {
      document.getElementById(`task-${detail.tasks[index]!._id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [detail]);




  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title={detail?.titleSnapshot ?? "Activity detail"}
        description={detail?.descriptionSnapshot ?? ""}
        loading={detail === undefined}
        meta={detail === undefined
          ? <Skeleton height="5" width={{ base: "64", sm: "sm" }} />
          : <ActivityFacts detail={detail} todayDay={todayDay} />}
        breadcrumbs={[
          { label: "CeMMbase", to: "/" },
          ...crumbs,
          { label: detail?.titleSnapshot ?? "Activity detail" },
        ]}
        actions={
          <>
            {detail?.subject && !detail.subject.isViewer ? <AssigneeBlock subject={detail.subject} /> : null}
            <Button asChild variant="outline">
              <Link to={back.to} search={back.search}>
                <LuArrowLeft />
                {back.label}
              </Link>
            </Button>
          </>
        }
      />

      <Stack gap={8} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {detail === undefined ? (
          <DetailSkeleton label="Loading the activity" sections={2} />
        ) : (
          <>
            <DependencyNote detail={detail} />

            <Stack as="section" gap={4}>
              <Heading size="xl">Steps</Heading>

              {/* A timeline, because the order is the point: each step
                  usually opens once the one before it is approved. */}
              <Timeline.Root size="xl" variant="solid">
                {detail.tasks.map((task) => (
                  <ActivityTaskCard
                    key={task._id}
                    task={task}
                    todayDay={todayDay}
                    // Whose record this is, for steps that belong to somebody
                    // other than them.
                    subjectName={detail.person
                      ? `${detail.person.firstName} ${detail.person.lastName}`
                      : null}
                    // The backend refuses a write to either, so the form must
                    // not invite one.
                    activityClosed={detail.status === "archived" || detail.status === "cancelled"}
                  />
                ))}
              </Timeline.Root>
            </Stack>

            <ActivityHistory history={detail.history} />
          </>
        )}
      </Stack>
    </Box>
  );
}
